/* eslint-disable max-lines -- one page wiring two filters, search, pagination, nested expansion and
   a shared column-visibility menu; the filter state, options, visibility and filter controls are
   already extracted, and what remains is a single DimeNestedTable prop list. */
import {
  buttonVariants,
  DimeCard,
  DimeCardAction,
  DimeCardDescription,
  DimeCardHeader,
  DimeCardTitle,
  DimeNestedTable,
  toast,
} from '@nudool/design-system';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Warehouse } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { ListEmptyState } from '../../components/ListEmptyState.js';
import { ListErrorState } from '../../components/ListErrorState.js';
import {
  parseListRouteSearch,
  type ListRouteSearch,
} from '../../hooks/use-list-table-helpers/index.js';
import { useListTable } from '../../hooks/use-list-table.js';
import { routeRequirements } from '../../lib/access/index.js';
import { buildListEmptyMessage } from '../../lib/build-list-empty-message.js';
import { isDefaultStatusSelection } from '../../lib/is-default-status-selection.js';
import { PermissionGuard } from '../../lib/permission-guard.js';
import { toActiveFlagEquals } from '../../lib/to-active-flag-equals.js';
import { toStatusScope } from '../../lib/to-status-scope.js';
import { usePermissions } from '../../modules/iam/permissions-context.js';
import {
  GET_WAREHOUSES_LIST,
  type GetWarehousesListQueryResult,
  type GetWarehousesListQueryVariables,
  type LocationGroupWhere,
} from '../../modules/location-groups/get-warehouses-list.js';
import { makeLocationGroupColumns } from '../../modules/location-groups/location-group-columns.js';
import type { LocationGroupListItem } from '../../modules/location-groups/operations.js';
import { useWarehouseColumnVisibility } from '../../modules/location-groups/use-warehouse-column-visibility.js';
import { useWarehouseListFilters } from '../../modules/location-groups/use-warehouse-list-filters.js';
import { useWarehouseStateOptions } from '../../modules/location-groups/use-warehouse-state-options.js';
import { useWarehouseUserOptions } from '../../modules/location-groups/use-warehouse-user-options.js';
import { WarehouseColumnsMenu } from '../../modules/location-groups/warehouse-columns-menu.js';
import { buildWarehouseListWhere } from '../../modules/location-groups/warehouse-list-where.js';
import { WarehouseStateFilter } from '../../modules/location-groups/warehouse-state-filter.js';
import { WarehouseUsersFilter } from '../../modules/location-groups/warehouse-users-filter.js';
import { buildLocationFiltersWhere } from '../../modules/locations/build-locations-for-group-where.js';
import { isLocationSelectionUnfiltered } from '../../modules/locations/is-location-selection-unfiltered.js';
import { LocationFiltersMenu } from '../../modules/locations/location-filters-menu.js';
import { LocationGroupNestedPanel } from '../../modules/locations/location-group-nested-panel.js';
import type { LocationListItem } from '../../modules/locations/operations.js';
import { useLocationFilters } from '../../modules/locations/use-location-filters.js';
import { useLocationFormOptions } from '../../modules/locations/use-location-form-options.js';
import { useLocationRowsByGroup } from '../../modules/locations/use-location-rows-by-group.js';

const WAREHOUSE_LIST_STALE_MESSAGE =
  'Warehouse list may be out of date — refresh the page to see the latest.';

export const WAREHOUSE_SORT_MAP: Record<string, string[]> = {
  name: ['name'],
  activeFlag: ['activeFlag'],
  state: ['state'],
  address: ['address'],
  users: ['users'],
  locationCount: ['locationCount'],
};

function warehouseListSortMap(canViewUsers: boolean): Record<string, string[]> {
  if (canViewUsers) return WAREHOUSE_SORT_MAP;
  return Object.fromEntries(
    Object.entries(WAREHOUSE_SORT_MAP).filter(([columnId]) => columnId !== 'users'),
  );
}

export const Route = createFileRoute('/warehouses/')({
  validateSearch: (search: Record<string, unknown>): ListRouteSearch =>
    parseListRouteSearch(search),
  component: WarehousesPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function WarehousesPage() {
  const { permissions } = usePermissions();
  const canEdit = permissions.includes('locationGroup:edit');
  const canAddLocation = permissions.includes('location:edit');
  const canViewLocations = permissions.includes('location:view');
  const canViewUsers = permissions.includes('user:view');
  const {
    activeStatuses: warehouseStatuses,
    stateIds,
    userIds,
    userActiveStatuses,
    onActiveStatusesChange: onWarehouseStatusesChange,
    onStateIdsChange,
    onUserIdsChange,
    onUserActiveStatusesChange,
  } = useWarehouseListFilters();
  // The location menu owns its own status: a warehouse's activeFlag and its bins' are independent,
  // so scoping one must not silently reclassify the other.
  const { activeStatuses, typeIds, flags, onActiveStatusesChange, onTypeIdsChange, onFlagChange } =
    useLocationFilters();
  const { locationTypes } = useLocationFormOptions(false, { skip: !canViewLocations });
  // locationCount, the chevron and the nested panel all read this one selection, so the number
  // cannot disagree with the rows behind it.
  const locationSelection = useMemo(
    () => ({ activeStatuses, typeIds, flags }),
    [activeStatuses, typeIds, flags],
  );
  // One object feeds the count argument and the nested query's where, so the Locations column is by
  // construction the number of rows its chevron opens.
  const locationFilterWhere = useMemo(
    () => buildLocationFiltersWhere(locationSelection),
    [locationSelection],
  );
  const stateOptions = useWarehouseStateOptions();
  const [usersFilterOpen, setUsersFilterOpen] = useState(false);
  const [userOptionSearch, setUserOptionSearch] = useState('');
  const userOptions = useWarehouseUserOptions({
    canViewUsers,
    enabled: canViewUsers && usersFilterOpen,
    search: userOptionSearch,
    activeStatuses: userActiveStatuses,
    selectedIds: userIds,
  });
  // Active is the default, and on it the User Status filter narrows nothing -- matching Location
  // Status, so an unstaffed warehouse is not dropped from a view nobody filtered.
  const userStatusHidesWarehouses =
    canViewUsers && toStatusScope(userActiveStatuses) === 'inactive';
  // WHY: the default Active-only status is not itself a "filter applied", matching users/index.tsx
  // -- it still reaches the filters branch below, which is the right advice either way.
  const hasActiveFilters =
    stateIds.length > 0 ||
    userIds.length > 0 ||
    typeIds.length > 0 ||
    Object.values(flags).some((flag) => flag?.length) ||
    !isDefaultStatusSelection(warehouseStatuses) ||
    !isDefaultStatusSelection(activeStatuses) ||
    !isDefaultStatusSelection(userActiveStatuses);
  const {
    childRowsByParentId,
    loadingParentIds,
    failedParentIds,
    hasMoreByParentId,
    fetchLocationsForGroup,
  } = useLocationRowsByGroup(locationSelection);

  const {
    rows: warehousesRaw,
    loading,
    error,
    refetch,
    tableProps,
    search,
  } = useListTable<
    GetWarehousesListQueryResult,
    GetWarehousesListQueryVariables,
    GetWarehousesListQueryResult['locationGroups']['edges'][number]['node'],
    LocationGroupWhere | undefined
  >({
    query: GET_WAREHOUSES_LIST,
    extractConnection: (data) => data?.locationGroups,
    defaultSort: [{ id: 'name', desc: false }],
    sortMap: warehouseListSortMap(canViewUsers),
    sortTiebreakerField: 'id',
    where: buildWarehouseListWhere(warehouseStatuses, {
      stateIds,
      locationSelection,
      userSelection: canViewUsers
        ? { activeStatuses: userActiveStatuses, userIds }
        : { activeStatuses: [], userIds: [] },
    }),
    variables: {
      locationWhere: locationFilterWhere,
      userActiveFlag: canViewUsers ? toActiveFlagEquals(userActiveStatuses)?.equals : undefined,
      canViewUsers,
      canEditLocationGroups: canEdit,
    },
  });

  const refetchWarehouses = useCallback(() => {
    void refetch().catch(() => {
      toast.error(WAREHOUSE_LIST_STALE_MESSAGE);
    });
  }, [refetch]);

  const noOtherFilters =
    !search &&
    stateIds.length === 0 &&
    userIds.length === 0 &&
    !userStatusHidesWarehouses &&
    isLocationSelectionUnfiltered({ activeStatuses, typeIds, flags });
  const noResultsMessage = buildListEmptyMessage('warehouses', search, hasActiveFilters);
  // Only an unfiltered view may show the full-page empty state, since it also hides the filters.
  const isUnfilteredView = noOtherFilters && toStatusScope(warehouseStatuses) === 'all';

  const warehouses: LocationGroupListItem[] = warehousesRaw.map((node) => ({
    ...node,
    address: node.address ?? [],
    userToLocationGroup: node.userToLocationGroup ?? undefined,
  }));
  // onStatusChanged refetches the active-only list so a deactivated warehouse's row disappears.
  const columns = useMemo(
    // WHY: onStatusChanged refetches the list so a status change moves the row in or out of the current filter.
    () =>
      makeLocationGroupColumns({
        canEdit,
        canAddLocation,
        canViewUsers,
        userActiveStatuses,
        onStatusChanged: refetchWarehouses,
      }),
    [canEdit, canAddLocation, canViewUsers, userActiveStatuses, refetchWarehouses],
  );
  const {
    warehouseColumnToggles,
    warehouseColumnVisibility,
    setWarehouseColumnVisibility,
    locationColumnVisibility,
    setLocationColumnVisibility,
  } = useWarehouseColumnVisibility(columns);

  const handleLocationStatusChanged = useCallback(
    // WHY: also refetches the outer list so its locationCount column stays in step with the filter.
    (locationGroupId: string) => {
      fetchLocationsForGroup(locationGroupId, undefined, locationSelection);
      refetchWarehouses();
    },
    [fetchLocationsForGroup, refetchWarehouses, locationSelection],
  );

  const newWarehouseAction = (
    <PermissionGuard
      required={routeRequirements('/warehouses/new')}
      pass={
        <Link to="/warehouses/new" className={buttonVariants({})}>
          + New Warehouse
        </Link>
      }
      fail={
        <span className={buttonVariants({})} role="link" aria-disabled="true">
          + New Warehouse
        </span>
      }
    />
  );

  return (
    <DimeCard borderless className="gap-0 py-0">
      <DimeCardHeader className="px-0 mb-4">
        <DimeCardTitle className="text-base flex items-center gap-2">Warehouses</DimeCardTitle>
        <DimeCardDescription className="text-sm">
          Sites and the locations they contain. Expand a warehouse to see its locations.
        </DimeCardDescription>
        <DimeCardAction>{newWarehouseAction}</DimeCardAction>
      </DimeCardHeader>

      {error && warehouses.length === 0 ? (
        <ListErrorState
          title="Could not load warehouses"
          message={error.message}
          onRetry={() => void refetch()}
        />
      ) : !loading && warehouses.length === 0 && isUnfilteredView ? (
        <ListEmptyState
          icon={<Warehouse />}
          title="No warehouses yet"
          description="Get started by creating your first warehouse or site."
          action={newWarehouseAction}
        />
      ) : (
        <DimeNestedTable<LocationGroupListItem, LocationListItem>
          columns={columns}
          data={warehouses}
          maxBodyHeight="viewport"
          getRowId={(warehouse) => warehouse.id}
          getRowCanExpand={(warehouse) => canViewLocations && warehouse.locationCount > 0}
          childRowsByParentId={childRowsByParentId}
          loadingParentIds={loadingParentIds}
          onExpandRow={(warehouse, reportFailed) =>
            fetchLocationsForGroup(warehouse.id, reportFailed, locationSelection)
          }
          renderChildRows={({ parent, children, isLoading: childrenLoading }) => (
            <LocationGroupNestedPanel
              parent={parent}
              rows={children}
              isLoading={childrenLoading}
              hasFailed={Boolean(failedParentIds[parent.id])}
              hasMore={Boolean(hasMoreByParentId[parent.id])}
              canEditLocation={canAddLocation}
              columnVisibility={locationColumnVisibility}
              onColumnVisibilityChange={setLocationColumnVisibility}
              onLocationStatusChanged={handleLocationStatusChanged}
              onRetry={(locationGroupId) =>
                fetchLocationsForGroup(locationGroupId, undefined, locationSelection)
              }
            />
          )}
          // Picked one by one, not spread: tableProps also carries export props DimeNestedTable has no slot for.
          hasNextPage={tableProps.hasNextPage}
          hasPreviousPage={tableProps.hasPreviousPage}
          onNextPage={tableProps.onNextPage}
          onPreviousPage={tableProps.onPreviousPage}
          onFirstPage={tableProps.onFirstPage}
          onLastPage={tableProps.onLastPage}
          pageIndex={tableProps.pageIndex}
          pageSize={tableProps.pageSize}
          pageSizeOptions={tableProps.pageSizeOptions}
          onPageSizeChange={tableProps.onPageSizeChange}
          totalCount={tableProps.totalCount}
          sortBy={tableProps.sortBy}
          onUpdateSortBy={tableProps.onUpdateSortBy}
          manualSorting={tableProps.manualSorting}
          searchValue={tableProps.searchValue}
          onSearchChange={tableProps.onSearchChange}
          isLoading={tableProps.isLoading}
          loadingRowCount={tableProps.pageSize}
          searchColumn="name"
          searchPlaceholder="Search by name…"
          filters={
            <>
              <WarehouseStateFilter
                states={stateOptions}
                value={stateIds}
                onChange={onStateIdsChange}
              />
              {canViewUsers ? (
                <WarehouseUsersFilter
                  users={userOptions}
                  value={userIds}
                  onChange={onUserIdsChange}
                  activeStatuses={userActiveStatuses}
                  onActiveStatusesChange={onUserActiveStatusesChange}
                  onOpenChange={(open) => {
                    if (open) setUsersFilterOpen(true);
                  }}
                  onSearchChange={setUserOptionSearch}
                />
              ) : null}
              <LocationFiltersMenu
                accessibleName="Location filters"
                warehouseStatuses={warehouseStatuses}
                onWarehouseStatusesChange={onWarehouseStatusesChange}
                activeStatuses={activeStatuses}
                onActiveStatusesChange={onActiveStatusesChange}
                types={canViewLocations ? locationTypes : []}
                typeIds={typeIds}
                onTypeIdsChange={onTypeIdsChange}
                flags={flags}
                onFlagChange={onFlagChange}
              />
            </>
          }
          columnVisibility={warehouseColumnVisibility}
          onColumnVisibilityChange={setWarehouseColumnVisibility}
          actions={
            <WarehouseColumnsMenu
              warehouseColumns={warehouseColumnToggles}
              warehouseColumnVisibility={warehouseColumnVisibility}
              onWarehouseColumnVisibilityChange={setWarehouseColumnVisibility}
              locationColumnVisibility={locationColumnVisibility}
              onLocationColumnVisibilityChange={setLocationColumnVisibility}
              showLocationColumns={canViewLocations}
            />
          }
          showExpandAll
          emptyMessage={noResultsMessage}
        />
      )}
    </DimeCard>
  );
}
